import { type Ref } from 'vue';

import type { CaseContact, CasePackageDimensions, CasePickup, ReturnCase } from '@/types/case.interface';
import { useFormat } from './format';

export function useCaseUpdater(returnCase: Ref<ReturnCase>, onUpdate: (updatedCase: ReturnCase) => void) {
  const { getSplittedCountryCode } = useFormat();

  const updateCaseField = <K extends keyof ReturnCase>(field: K, value: ReturnCase[K]) => {
    const updatedCase = { ...returnCase.value, [field]: value };
    onUpdate(updatedCase);
  };

  const updatePickupField = <K extends keyof CasePickup>(field: K, value: CasePickup[K]) => {
    const updatedPickup = { ...returnCase.value.pickup, [field]: value } as CasePickup;
    updateCaseField('pickup', updatedPickup);
  };

  const updatePickupContactField = <K extends keyof CaseContact>(field: K, value: CaseContact[K]) => {
    const updatedContact = { ...returnCase.value?.pickup?.contact, [field]: value } as CaseContact;
    updatePickupField('contact', updatedContact);
  };

  const updatePickupCallInfo = (callingCountryCode: string) => {
    const [countryCode, country] = getSplittedCountryCode(callingCountryCode);

    const updatedContact = {
      ...returnCase.value?.pickup?.contact,
      countryCode: `+${countryCode}`,
      country,
    } as CaseContact;
    updatePickupField('contact', updatedContact);
  };

  const updatePickupDimensionField = <K extends keyof CasePackageDimensions>(
    field: K,
    value: CasePackageDimensions[K],
  ) => {
    const updatedDimensions = { ...returnCase.value?.pickup?.dimensions, [field]: value } as CasePackageDimensions;
    updatePickupField('dimensions', updatedDimensions);
  };

  return {
    updatePickupField,
    updateCaseField,
    updatePickupContactField,
    updatePickupCallInfo,
    updatePickupDimensionField,
  };
}
